import { MapPin } from 'lucide-react'
import {
	type GeoPermission,
	type GeoState,
	openLocationSettings,
} from '@hooks/useGeolocation.ts'
import styles from './LocationStatus.module.css'

type LocateButtonProps = {
	onClick: () => void
}

export const LocateButton = ({ onClick }: LocateButtonProps) => (
	<button type="button" className={styles.locate} onClick={onClick}>
		<MapPin size={16} aria-hidden="true" />
		Use my location for nearby ideas and today’s weather
	</button>
)

type Props = {
	geo: GeoState
	permission?: GeoPermission | null
	onRetry: () => void
}

// Progress or failure of a location lookup. A failure always offers a retry.
// A denied permission counts as a failure before any lookup has run.
export const LocationStatus = ({ geo, permission = null, onRetry }: Props) => {
	if (geo.status === 'prompting') {
		return <p className={styles.note}>Getting your location…</p>
	}
	const denied =
		geo.status === 'denied' ||
		(permission === 'denied' && geo.status !== 'granted')
	if (!denied && geo.status !== 'unavailable') return null

	const retry = (
		<button type="button" className={styles.retry} onClick={onRetry}>
			Try again
		</button>
	)

	if (!denied) {
		return (
			<p className={styles.note}>
				Your location couldn’t be found. {retry}
			</p>
		)
	}

	if (openLocationSettings) {
		return (
			<p className={styles.note}>
				Location access is turned off for Hewey. Turn it on in Settings,
				then try again.{' '}
				<button
					type="button"
					className={styles.retry}
					onClick={openLocationSettings}
				>
					Open Settings
				</button>{' '}
				· {retry}
			</p>
		)
	}

	return (
		<p className={styles.note}>
			Location access is blocked for this site. Allow it in your browser’s
			settings, then try again. {retry}
		</p>
	)
}
